import React, { useCallback, useEffect, useState } from 'react';
import { Plus, MessageSquare } from 'lucide-react';
import { api } from '../../../lib/api';
import TimelineEntry from '../../../components/crm/TimelineEntry';
import FilterChip from '../../../components/crm/FilterChip';
import InteractionLogger from '../../../components/crm/InteractionLogger';
import EmptyState from '../../../components/crm/EmptyState';
import ConfirmDialog from '../../../components/admin/ConfirmDialog';

const FILTERS = [
  ['all', 'All'],
  ['interaction', 'Interactions'],
  ['stage_history', 'Stage history'],
  ['stage_marker', 'Current stage'],
  ['task_update', 'Tasks'],
];

export default function TimelineTab({ account }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [loggerOpen, setLoggerOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/accounts/${account.id}/timeline`);
      setEvents(res.timeline || []);
    } finally {
      setLoading(false);
    }
  }, [account.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = (() => {
    if (filter === 'all') return events;
    if (filter === 'stage_history') {
      // Synthetic filter: stage transitions are stored as interactions with
      // stage_from/stage_to set. Show only those.
      return events.filter((e) => e.kind === 'interaction' && e.interaction?.stageFrom && e.interaction?.stageTo);
    }
    if (filter === 'interaction') {
      // Interactions filter excludes stage transitions — those have their
      // own filter chip — so this view is just the free-form notes.
      return events.filter((e) => e.kind === 'interaction' && !(e.interaction?.stageFrom && e.interaction?.stageTo));
    }
    return events.filter((e) => e.kind === filter);
  })();

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          {FILTERS.map(([v, l]) => (
            <FilterChip key={v} active={filter === v} onClick={() => setFilter(v)}>{l}</FilterChip>
          ))}
        </div>
        <button
          onClick={() => setLoggerOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900"
        >
          <Plus className="w-3 h-3" /> Log interaction
        </button>
      </div>

      {!loading && filtered.length === 0 ? (
        <EmptyState icon={MessageSquare} title="No events yet" description="Log an interaction to start the timeline." />
      ) : (
        <div className="space-y-3">
          {filtered.map((e, i) => (
            <TimelineEntry
              key={`${e.kind}-${i}-${e.at}`}
              event={e}
              onDelete={e.kind === 'interaction' ? () => setPendingDelete(e.interaction) : undefined}
            />
          ))}
        </div>
      )}

      {deleteError && (
        <div className="mt-3 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-[12px] text-red-300">
          {deleteError}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => { setPendingDelete(null); setDeleteError(''); }}
        onConfirm={async () => {
          if (!pendingDelete) return;
          try {
            await api.delete(`/api/admin/interactions/${pendingDelete.id}`);
            setPendingDelete(null);
            setDeleteError('');
            await load();
          } catch (err) {
            setDeleteError(err?.message || 'Delete failed');
          }
        }}
        title="Delete this interaction?"
        description={
          pendingDelete?.stageFrom && pendingDelete?.stageTo
            ? `Permanently removes the timeline entry for the ${String(pendingDelete.stageFrom).replace(/_/g, ' ')} → ${String(pendingDelete.stageTo).replace(/_/g, ' ')} stage change. The account's current stage is unchanged; only the audit-trail entry is removed.`
            : "Permanently removes this interaction from the timeline. This can't be undone."
        }
        confirmLabel="Delete"
        destructive
      />

      {loggerOpen && (
        <InteractionLogger
          open
          accountId={account.id}
          onClose={() => setLoggerOpen(false)}
          onLogged={() => { setLoggerOpen(false); load(); }}
        />
      )}
    </div>
  );
}
